// Read: https://vite.dev/guide/features.html#web-workers
import ServiceWorker from '~/workers/service-worker?worker'
import { tryOnScopeDispose } from '@vueuse/core'
import type { MapAssetObject } from '~/types/map-assets.types'
import type { Route } from '~/types/routes'
import type { GenericObject } from '~/types/common'

// Auth context for worker requests
export interface WorkerAuthContext {
  accessToken: string
  licenseId: number | null
  siteId: number | null
  apiUrl: string
}

// Dictionary of event types -> payload types
export interface WorkerMessageMap {
  // Existing messages
  mapAssetsFetched: {
    hashes: string[]
    assets: MapAssetObject[]
    licenseId: number | null
  }
  createSectionsRouteMap: {
    routes: Route[]
  }
  sectionsRouteMapCreated: {
    sectionRouteMap: [number, number][]
  }

  // Progressive loading messages (main thread → worker)
  loadAssetsProgressively: {
    requestId: string
    originHash: string
    precision: number | null
    excludeOrigins: string[]
    authContext: WorkerAuthContext
  }

  // Streaming responses (worker → main thread)
  assetsCacheChunk: {
    requestId: string
    originHash: string
    assets: MapAssetObject[]
    source: 'cache'
  }
  assetsServerChunk: {
    requestId: string
    originHash: string
    assets: MapAssetObject[]
    source: 'server'
  }

  // Completion signal (worker → main thread)
  loadAssetsStepComplete: {
    requestId: string
    originHash: string
    cachedCount: number
    serverCount: number
  }

  // Error response (worker → main thread)
  loadAssetsError: {
    requestId: string
    error: string
    retryable: boolean
  }

  // Health check
  ping: Record<string, never>
  pong: Record<string, never>
}

type WorkerMessage<K extends keyof WorkerMessageMap = keyof WorkerMessageMap> = {
  [T in K]: { type: T; payload: WorkerMessageMap[T] }
}[K]

type MessageListener = (event: MessageEvent) => void

interface ServiceWorkerComposable {
  sendMessage: (msg: WorkerMessage) => void
  on: (message: string, handler: (payload: GenericObject) => void) => () => void
  healthCheck: () => Promise<boolean>
  isHealthy: () => boolean
}

// Module-level shared worker state
let sharedWorker: Worker | null = null
let refCount = 0
const listeners: Map<MessageListener, string> = new Map()
let workerHealthy = true
const HEALTH_CHECK_TIMEOUT_MS = 5000

const getWorker = (): Worker => {
  if (!sharedWorker) {
    sharedWorker = new ServiceWorker()
  }
  refCount++
  return sharedWorker
}

const releaseWorker = (listenersToRemove: MessageListener[]): void => {
  if (sharedWorker) {
    listenersToRemove.forEach((listener) => {
      sharedWorker?.removeEventListener('message', listener)
      listeners.delete(listener)
    })
  }

  refCount--
  if (refCount <= 0 && sharedWorker) {
    sharedWorker.terminate()
    sharedWorker = null
    refCount = 0
    listeners.clear()
  }
}

export const useServiceWorker = (): ServiceWorkerComposable => {
  const worker = getWorker()
  const localListeners: MessageListener[] = []

  const sendMessage = (msg: WorkerMessage): void => {
    worker.postMessage(msg)
  }

  const on = (message: string, handler: (payload: GenericObject) => void): (() => void) => {
    const listener: MessageListener = (event: MessageEvent) => {
      if (event.data.type === message) {
        handler(event.data.payload)
      }
    }

    worker.addEventListener('message', listener)
    listeners.set(listener, message)
    localListeners.push(listener)

    return () => {
      worker.removeEventListener('message', listener)
      listeners.delete(listener)
      const index = localListeners.indexOf(listener)
      if (index > -1) {
        localListeners.splice(index, 1)
      }
    }
  }

  const healthCheck = async (): Promise<boolean> => {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        workerHealthy = false
        resolve(false)
      }, HEALTH_CHECK_TIMEOUT_MS)

      const unsub = on('pong', () => {
        clearTimeout(timeout)
        workerHealthy = true
        unsub()
        resolve(true)
      })

      sendMessage({ type: 'ping', payload: {} })
    })
  }

  const isHealthy = (): boolean => workerHealthy

  tryOnScopeDispose(() => {
    releaseWorker(localListeners)
  })

  return {
    sendMessage,
    on,
    healthCheck,
    isHealthy
  }
}
